import { IdHelper } from '../helpers/id.helper';
import type { ILine } from '../models/line.model';
import type { IMunicipality } from '../models/municipality.model';
import type { ITrafficSituation } from '../models/traffic-situation.model';
import type { TransportMode } from '../models/transport-mode.enum';
import { ClientSideStorageItems, ClientSideStorageService } from './client-side-storage.service';
import { ConfigurationService } from './configuration.service';

enum UserFeatureName {
  municipality = 'm',
  line = 'l',
  transportMode = 'tm',
}

export class TrafficInformationFeedbackService {
  private static enabled: boolean | undefined = undefined;
  private static userId: string | undefined;

  // New properties for batching
  private static feedbackQueue: { trafficSituationNumbers?: string[]; userFeatures?: { name: string; value: string; weight: number }[] }[] = [];
  private static batchInterval: number = 5000; // 5 seconds
  private static batchSize: number = 10; // Maximum items per batch
  private static batchTimer: NodeJS.Timeout | undefined;

  // Add a static initializer to set up the event listener
  static {
    document.addEventListener('visibilitychange', function logData() {
      if (document.visibilityState === 'hidden') {
        TrafficInformationFeedbackService.flushQueue();
      }
    });
  }

  public static isEnabled() {
    if (this.enabled === undefined) {
      this.enabled = ConfigurationService.get('featureflags.trafficinformation.feedbackcollection') === 'True';
    }
    return this.enabled;
  }

  public static async registerExplicitFeedback(trafficSituation: ITrafficSituation) {
    if (!TrafficInformationFeedbackService.isEnabled()) return;
    TrafficInformationFeedbackService.addToQueue({ trafficSituationNumbers: [trafficSituation.situationNumber] });
  }

  public static registerSelectedLine(line: ILine): void {
    if (!TrafficInformationFeedbackService.isEnabled() || !line.gid) return;
    TrafficInformationFeedbackService.addToQueue({ userFeatures: [{ name: UserFeatureName.line, value: line.gid, weight: 1 }] });
  }

  public static registerSelectedMunicipalities(oldMunicipalities: IMunicipality[], newMunicipalities: IMunicipality[]): void {
    if (!TrafficInformationFeedbackService.isEnabled()) return;
    const addedMunicipalities = newMunicipalities.filter((m) => !oldMunicipalities.includes(m));
    const removedMunicipalities = oldMunicipalities.filter((m) => !newMunicipalities.includes(m));
    addedMunicipalities.forEach((m) => {
      TrafficInformationFeedbackService.addToQueue({ userFeatures: [{ name: UserFeatureName.municipality, value: m.gid, weight: 1 }] });
    });
    removedMunicipalities.forEach((m) => {
      TrafficInformationFeedbackService.addToQueue({ userFeatures: [{ name: UserFeatureName.municipality, value: m.gid, weight: -1 }] });
    });
  }

  public static registerSelectedTransportModes(oldTransportModes: TransportMode[], newTransportModes: TransportMode[]): void {
    if (!TrafficInformationFeedbackService.isEnabled()) return;
    const addedTransportModes = newTransportModes.filter((m) => !oldTransportModes.includes(m));
    const removedTransportModes = oldTransportModes.filter((m) => !newTransportModes.includes(m));
    addedTransportModes.forEach((m) => {
      TrafficInformationFeedbackService.addToQueue({ userFeatures: [{ name: UserFeatureName.transportMode, value: m, weight: 1 }] });
    });
    removedTransportModes.forEach((m) => {
      TrafficInformationFeedbackService.addToQueue({ userFeatures: [{ name: UserFeatureName.transportMode, value: m, weight: -1 }] });
    });
  }

  private static addToQueue(data: { trafficSituationNumbers?: string[]; userFeatures?: { name: string; value: string; weight: number }[] }) {
    this.feedbackQueue.push(data);

    if (this.feedbackQueue.length >= this.batchSize) {
      this.flushQueue();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.flushQueue(), this.batchInterval);
    }
  }

  private static flushQueue() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }

    if (this.feedbackQueue.length === 0) return;

    const batch = this.feedbackQueue.splice(0, this.feedbackQueue.length);
    const trafficSituationNumbers = batch.flatMap((item) => item.trafficSituationNumbers || []);
    const userFeatures = batch.flatMap((item) => item.userFeatures || []);

    if (trafficSituationNumbers.length > 0 || userFeatures.length > 0) {
      TrafficInformationFeedbackService.sendFeedbackDataToServer(trafficSituationNumbers, userFeatures);
    }
  }

  private static getUserId(): string | undefined {
    if (!ClientSideStorageService.isConsentGiven(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId.consentStatus)) return undefined;

    if (this.userId) return this.userId;

    try {
      this.userId = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId);
      if (this.userId) return this.userId;
    } catch {
      /* empty */
    }

    try {
      this.userId = IdHelper.newGuid();
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId, this.userId);
      return this.userId;
    } catch {
      return undefined;
    }
  }

  private static sendFeedbackDataToServer(trafficSituationNumbers: string[], userFeatures: { name: string; value: string; weight: number }[]) {
    const userId = TrafficInformationFeedbackService.getUserId();
    if (!userId) return;

    navigator.sendBeacon(
      '/api/trafficsituations/feedback',
      JSON.stringify({
        u: userId,
        s: trafficSituationNumbers,
        f: userFeatures.map((x) => ({ f: x.name, v: x.value, w: x.weight })),
      })
    );
  }
}
